// src/context/LanguageContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
    currentLanguage: string;
    changeLanguage: (lang: string) => void;
    availableLanguages: { code: string; name: string; flag: string }[];
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const languages = [
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
];

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { i18n } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'vi');

    useEffect(() => {
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage && languages.find(lang => lang.code === savedLanguage)) {
            changeLanguage(savedLanguage);
        }
    }, []);

    const changeLanguage = async (lang: string) => {
        try {
            await i18n.changeLanguage(lang);
            setCurrentLanguage(lang);
            localStorage.setItem('language', lang);
            // Cập nhật HTML direction cho RTL languages nếu cần
            document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error changing language:', error);
        }
    };

    const value = {
        currentLanguage,
        changeLanguage,
        availableLanguages: languages,
    };

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};